import { useTranslation } from '@mint-lib/i18n';
import { toast } from '@mint-lib/notifications';
import { getRouteApi } from '@mint/routing';

import {
  useCreateInsertionOrderMutation,
  useLazyGetInsertionOrderInfoQuery,
} from '../api/insertionOrderApi.js';
import { useCampaignDataSelector } from '../hooks/useGetCampaignData.js';
import type { IOId } from '../types/insertionOrder.js';
import type { CustomError } from '../types/shared.js';
import { handleInsertionOrderError } from '../utils/campaignsBuilder.js';
import { useSetInsertionOrderLoading } from './useInsertionOrderLoading.js';
import { useInsertionOrderValidation } from './useInsertionOrderValidation.js';
import useSimpleNotification from './useSImpleNotification.js';

type DataForSuccess = {
  ioData: IOId[];
  buyingPlanId: number;
  buyingPlanItemId: number;
  notificationId: number | string;
};

type GetIOInfo = {
  buyingPlanId: number;
  buyingPlanItemId: number;
  isPlatformLevel: boolean;
};

const routeApi = getRouteApi(
  '/_secured/campaign/$campaignId/planning/buying-plan',
);

export const useInsertionOrderApi = () => {
  const { t } = useTranslation('@myn/planning');

  const navigate = routeApi.useNavigate();

  const { data: currentJourneyId } = useCampaignDataSelector('id');

  const setIsInsertionOrderLoading = useSetInsertionOrderLoading();

  const [getInsertionOrderInfo] = useLazyGetInsertionOrderInfoQuery();
  const [createNewInsertionOrderEntry] = useCreateInsertionOrderMutation();

  const { handleInsertionOrderValidation } = useInsertionOrderValidation();
  const { showSuccessNotification, showErrorNotification } =
    useSimpleNotification();

  const onSuccess = async (data: DataForSuccess, isPlatformLevel: boolean) => {
    const { notificationId, ...dataForValidation } = data;
    await handleInsertionOrderValidation(
      dataForValidation,
      isPlatformLevel,
      handleInsertionOrderEntryCreation,
    );
    closeNotification(notificationId);
  };

  const onEntryCreationSuccess = (ioEntryId: number, buyingPlanId: number) => {
    setIsInsertionOrderLoading(false);
    navigate({
      to: '/campaign/$campaignId/planning/insertion-orders/$id/',
      params: {
        campaignId: String(currentJourneyId),
        id: String(ioEntryId),
      },
      search: {
        mediaplan: buyingPlanId,
      },
    });
  };

  const onError = (error: CustomError) => {
    setIsInsertionOrderLoading(false);
    const newError = handleInsertionOrderError(error, t);
    showErrorNotification({ subTitle: newError.message });
  };

  const closeNotification = (notificationId: number | string) =>
    toast.dismiss(notificationId);

  const handleInsertionOrderInfoRequest = async ({
    buyingPlanId,
    buyingPlanItemId,
    isPlatformLevel,
  }: GetIOInfo) => {
    setIsInsertionOrderLoading(true);
    const notificationId = showSuccessNotification({
      title: t('We are processing your Insertion Order data...'),
    });
    const { data, isError, error } = await getInsertionOrderInfo(
      {
        buyingPlanId,
        buyingPlanItemId,
      },
      false,
    );

    if (isError) {
      closeNotification(notificationId);
      onError(error as CustomError);
      return;
    }

    const dataForSuccess = {
      ioData: data || [],
      buyingPlanId,
      buyingPlanItemId,
      notificationId,
    };

    onSuccess(dataForSuccess, isPlatformLevel);
  };

  const handleInsertionOrderEntryCreation = async (data: {
    ioId?: number | null;
    buyingPlanId: number;
    buyingPlanItemId: number;
  }) => {
    try {
      const { ioEntryId } = await createNewInsertionOrderEntry(data).unwrap();

      onEntryCreationSuccess(ioEntryId, data.buyingPlanId);
    } catch (error) {
      onError(error as CustomError);
    }
  };

  return {
    handleInsertionOrderInfoRequest,
    handleInsertionOrderEntryCreation,
  };
};
